import type { LegalBlock, LegalSection } from "@serophin/shared";
import { CONTACT_US, COOKIES_ANCHOR, PRIVACY_POLICY } from "./app";

export const TERMS_AGREEMENT: LegalBlock[] = [
  'Welcome to !tldr. By accessing or using www.nottldr.com or the !tldr apps (the "Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, please do not use our Service.',
  "These Terms affect your legal rights and obligations. If you do not agree to be bound by all of these Terms, do not access or use the Service.",
];

export const TERMS_SERVICE: LegalSection[] = [
  {
    heading: "Description of Service",
    blocks: [
      "!tldr provides AI-generated articles designed for mobile reading. Our AI creates original explanations on various topics to make complex ideas accessible and engaging.",
      "Creating an account unlocks personalization features including bookmarking, collections, and personalized recommendations.",
    ],
  },
  {
    heading: "AI-Generated Content",
    blocks: [
      "Our articles are generated using artificial intelligence and feature distinct author personas (neuroscientists, philosophers, historians, etc.). While these personas provide consistent voice and unique perspectives, they are AI-generated characters, not real individuals.",
      "All content is AI-generated and published directly without human review. While we use advanced AI systems, we cannot guarantee that all information is complete, accurate, or current. Users should verify important information independently. Our content is for informational purposes only and should not be considered professional advice.",
    ],
  },
  {
    heading: "User Accounts",
    blocks: [
      "To access certain features, you may need to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.",
      "You must be at least 13 years old to create an account. If you are under 18, you must have parental or guardian consent to use the Service. We comply with the Children's Online Privacy Protection Act (COPPA) and do not knowingly collect personal information from children under 13.",
    ],
  },
  {
    heading: "User-Generated Content",
    blocks: [
      'If you submit, post, or display any content, feedback, suggestions, or other materials ("User Content") through the Service, you grant us a worldwide, non-exclusive, royalty-free, perpetual, irrevocable license to use, reproduce, modify, adapt, publish, translate, distribute, and display such content.',
      "You represent and warrant that you own or have the necessary rights to submit User Content and that it does not violate any third-party rights or applicable laws.",
      "We reserve the right to remove any User Content that violates these Terms or that we find objectionable in our sole discretion.",
    ],
  },
  {
    heading: "Intellectual Property",
    blocks: [
      "All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by Serophin or our licensors and are protected by copyright, trademark, and other intellectual property laws.",
      "You may access and use the content for personal, non-commercial purposes only. You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any content without our express written permission.",
    ],
  },
  {
    heading: "Prohibited Uses",
    blocks: [
      "You agree not to:",
      {
        list: [
          "Use the Service for any illegal purpose or in violation of any laws",
          "Attempt to gain unauthorized access to our systems or user accounts",
          "Interfere with or disrupt the Service or servers",
          "Use automated systems to access or scrape content from the Service without our express written permission",
          "Impersonate any person or entity or misrepresent your affiliation",
          "Remove or modify any copyright, trademark, or proprietary notices",
          "Upload or transmit viruses or malicious code",
          "Harass, abuse, or harm other users",
          "Engage in any activity that could damage our reputation or business",
        ],
      },
    ],
  },
  {
    heading: "Privacy",
    blocks: [
      `Your use of the Service is also governed by our ${PRIVACY_POLICY}, which describes how we collect, use, and protect your personal information. By using the Service, you consent to our privacy practices.`,
      `We retain user data in accordance with our ${PRIVACY_POLICY}. You can delete your account and request deletion of your personal data at any time from your account settings. For additional assistance, you may ${CONTACT_US}.`,
    ],
  },
  {
    heading: "Third-Party Services",
    blocks: [
      "Our Service uses third-party services to enhance functionality:",
      {
        list: [
          "**AI-Generated Content:** Articles are generated using Anthropic's Claude AI (Claude Opus model), third-party artificial intelligence services. We may utilize additional AI services to enhance content quality.",
          "**AI-Generated Audio:** Articles include AI-generated audio versions for listening",
        ],
      },
      "These services have their own privacy policies and terms of use. By using our Service, you acknowledge that your data will be processed by these third-party services.",
    ],
  },
  {
    heading: "Cookies",
    blocks: [
      `We use essential cookies to keep you logged in and provide our Service. By using !tldr, you consent to our use of cookies as described in our [Cookie Policy](/privacy#${COOKIES_ANCHOR}).`,
    ],
  },
];
